import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { SchedulingSubject } from '../../interfaces/academic-appointment';
import { LocalizedLabelService } from './localized-label.service';

/** Matières d'un même parcours, telles qu'un formulaire les présente. */
export interface SubjectPathGroup {
  pathId: number;
  /** Vide si le parcours n'a pas été chargé : le groupe reste affichable. */
  pathLabel: string;
  subjects: SchedulingSubject[];
}

/**
 * Choisir une matière sans se tromper de niveau.
 *
 * <p>Le référentiel porte une ligne par couple matière-parcours : l'anglais de
 * 6ème primaire, celui de 9ème de base et celui du baccalauréat sont trois
 * matières distinctes. Toute liste qui couvre plusieurs parcours affiche donc
 * des entrées rigoureusement identiques, et le choix se fait au hasard.</p>
 *
 * <p>La règle vit ici plutôt que dans chaque écran : elle est déjà nécessaire à
 * l'agenda du professeur, à celui de l'administration et à la bibliothèque, et
 * trois copies auraient fini par diverger — c'est exactement ce qui était arrivé
 * aux deux projections du référentiel côté serveur.</p>
 *
 * <p>Les listes déjà restreintes à un parcours — celles des écrans élèves, ou
 * celles qu'un menu « parcours » filtre en amont — n'ont rien à en attendre :
 * le parcours y serait répété à chaque ligne sans jamais rien distinguer.</p>
 */
@Injectable({ providedIn: 'root' })
export class SubjectChoiceService {
  private readonly labels = inject(LocalizedLabelService);
  private readonly translate = inject(TranslateService);

  /**
   * Noms ambigus, mémorisés par liste et par langue.
   *
   * <p>Sans mémorisation, étiqueter une liste coûterait un parcours complet par
   * ligne. Le cache est faible : il ne retient pas une liste que l'écran a
   * remplacée.</p>
   */
  private readonly ambiguous = new WeakMap<
    readonly SchedulingSubject[],
    { lang: string; names: Set<string> }
  >();

  /**
   * Libellé d'une matière, situé par son parcours <b>seulement</b> s'il est
   * ambigu dans la liste où il figure.
   *
   * <p>Ailleurs, le parcours n'apporte rien et allonge une liste qui doit rester
   * lisible.</p>
   */
  label(subject: SchedulingSubject, within: readonly SchedulingSubject[]): string {
    const name = this.labels.pick(subject);
    if (!this.ambiguousNames(within).has(name)) return name;

    const path = this.pathLabel(subject);
    return path ? `${name} — ${path}` : name;
  }

  /**
   * Même règle, sur des libellés déjà résolus.
   *
   * <p>Toutes les listes de matières ne viennent pas du référentiel de
   * planification : celle des tests est reconstituée depuis les tests
   * eux-mêmes, celle de la banque de questions depuis la taxonomie. Elles ont
   * pourtant le même défaut et méritent la même correction — d'où cette entrée
   * qui ne demande qu'un nom et un titre de parcours, et non un type précis.</p>
   *
   * <p>Rend les libellés dans l'ordre reçu : à l'appelant de les recoller à ses
   * propres identifiants.</p>
   */
  disambiguate(
    items: readonly { name: string; pathLabel?: string | null }[],
  ): string[] {
    const seen = new Set<string>();
    const ambiguous = new Set<string>();

    for (const item of items) {
      if (seen.has(item.name)) ambiguous.add(item.name);
      else seen.add(item.name);
    }

    return items.map((item) =>
      ambiguous.has(item.name) && item.pathLabel
        ? `${item.name} — ${item.pathLabel}`
        : item.name,
    );
  }

  /**
   * Matières d'une séance, situées entre elles quand elles sont homonymes.
   *
   * <p>Repli sur le nom historique de la matière principale : une séance dont
   * la liste n'a pas été rendue garde au moins sa matière.</p>
   */
  subjectsOf(session: {
    subjects?: readonly SchedulingSubject[] | null;
    subjectName?: string | null;
  }): string {
    const list = session.subjects ?? [];
    if (list.length === 0) return session.subjectName ?? '';
    return list.map((s) => this.label(s, list)).join(' · ');
  }

  /**
   * Niveaux d'une séance : les parcours de ses matières, sans doublon, dans
   * l'ordre où la séance les porte — la principale d'abord.
   *
   * <p>C'est le niveau qui dit pour quelle classe la séance a lieu. Le nom du
   * groupe ne le remplace pas : une séance individuelle n'en a pas, et la
   * ligne restait alors vide.</p>
   */
  levelsOf(subjects: readonly SchedulingSubject[] | null | undefined): string {
    const levels = (subjects ?? []).map((s) => this.pathLabel(s)).filter(Boolean);
    return [...new Set(levels)].join(' · ');
  }

  /** Titre du parcours d'une matière, dans la langue active. */
  pathLabel(subject: SchedulingSubject): string {
    return this.labels.pickPrefixed(
      subject as unknown as Record<string, unknown>,
      'learningPathTitle',
    );
  }

  /** Noms portés par plus d'une matière de la liste. */
  ambiguousNames(within: readonly SchedulingSubject[]): Set<string> {
    const lang = this.translate.currentLang || 'ar';

    const cached = this.ambiguous.get(within);
    if (cached && cached.lang === lang) return cached.names;

    const seen = new Set<string>();
    const names = new Set<string>();

    for (const s of within) {
      const name = this.labels.pick(s);
      if (seen.has(name)) names.add(name);
      else seen.add(name);
    }

    this.ambiguous.set(within, { lang, names });
    return names;
  }

  /**
   * Matières groupées par parcours.
   *
   * <p>Grouper plutôt que filtrer : une séance peut porter le même cours pour
   * plusieurs niveaux, et un filtre imposerait un aller-retour par niveau pour
   * composer ce qui doit se lire d'un coup.</p>
   *
   * <p>Tri par identifiant de parcours : il suit l'ordre du cursus, là où un tri
   * sur le titre dépendrait de la langue affichée.</p>
   */
  groupByPath(subjects: readonly SchedulingSubject[]): SubjectPathGroup[] {
    const byPath = new Map<number, SubjectPathGroup>();

    for (const s of subjects) {
      const known = byPath.get(s.learningPathId);
      if (known) {
        known.subjects.push(s);
        continue;
      }
      byPath.set(s.learningPathId, {
        pathId: s.learningPathId,
        pathLabel: this.pathLabel(s),
        subjects: [s],
      });
    }

    return [...byPath.values()].sort((a, b) => a.pathId - b.pathId);
  }
}
